import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Clock, Share2, Tag, Facebook, MessageCircle, Copy } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useContent } from '@/hooks/useContent';
import { useLanguage } from '@/hooks/useLanguage';
import { useRTLArrow } from '@/hooks/useRTL';
import { useToast } from '@/hooks/useToast';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime?: number;
  content?: string[];
  image?: string;
}

const BlogPost = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useContent();
  const { isRTL, language } = useLanguage();
  const { showToast } = useToast();
  const { getArrow } = useRTLArrow();
  const BackArrow = getArrow('left');

  // Get all blog posts from content
  const allPosts = useMemo(() => {
    const posts = t('blog.posts', { returnObjects: true }) as BlogPost[];
    return Array.isArray(posts) ? posts : [];
  }, [t]);

  // Get categories
  const categories = useMemo(() => {
    const cats = t('blog.categories', { returnObjects: true }) as Record<string, string>;
    return cats || {};
  }, [t]);

  // Find the current post
  const post = useMemo(() => {
    return allPosts.find((p) => p.id === Number(id));
  }, [allPosts, id]);

  // Get related posts (same category, excluding current)
  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return allPosts
      .filter((p) => p.category === post.category && p.id !== post.id)
      .slice(0, 3);
  }, [allPosts, post]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} - ${t('common.brandName')}`;
    } else {
      document.title = `${t('blog.title')} - ${t('common.brandName')}`;
    }
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [post, t]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryName = (categoryKey: string) => {
    return categories[categoryKey] || categoryKey;
  };

  const buildShareText = (url: string) => {
    if (!post) return '';
    const shareCta = (t('blog.shareCta') as string) || 'To know more just visit';
    const segments: string[] = [];
    if (post.excerpt) {
      segments.push(post.excerpt);
    }
    if (post.content && post.content.length > 0) {
      segments.push(...post.content);
    }

    if (segments.length === 0) {
      return `🧪✨ ${shareCta} ${url} 🔗`;
    }

    let combined = '';
    for (const segment of segments) {
      const trimmed = segment?.trim();
      if (!trimmed) continue;
      combined += (combined ? ' ' : '') + trimmed;
      if (combined.length >= 220) break;
    }

    const preview = combined.slice(0, 200).trim();
    const ellipsis = combined.length > 200 ? '...' : '';
    return `🧪✨ ${preview}${ellipsis}\n${shareCta} ${url} 🔗`;
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = useMemo(() => buildShareText(currentUrl), [post, t, currentUrl]);
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedShareText = encodeURIComponent(shareText);
  const defaultPlaceholderImage = '/assets/images/elhamylab1024.jpeg';

  const articleGallery = useMemo(() => {
    if (!post) return null;
    switch (post.id) {
      case 1:
        return [
          {
            src: '/assets/images/CBC.png',
            alt: language === 'ar' ? 'مخطط تحاليل صورة الدم الكاملة' : 'CBC blood test infographic',
          },
          {
            src: '/assets/images/web.png',
            alt: language === 'ar' ? 'مؤشرات تحليل مكونات الدم' : 'Blood components visualization',
          },
        ];
      case 2:
        return [
          {
            src: '/assets/images/3d-render.png',
            alt: language === 'ar' ? 'طبيب بتقنية ثلاثية الأبعاد' : '3D render of medical professional',
          },
          {
            src: '/assets/images/pngtree-professional.png',
            alt: language === 'ar' ? 'طاقم طبي محترف' : 'Professional healthcare banner',
          },
        ];
      case 3:
        return [
          {
            src: '/assets/images/3d-blood-sugar.png',
            alt: language === 'ar' ? 'مجسم ثلاثي الأبعاد لقياس سكر الدم' : '3D blood sugar concept illustration',
          },
          {
            src: '/assets/images/diabetes-3d-concept.png',
            alt: language === 'ar' ? 'مفهوم ثلاثي الأبعاد لإدارة السكري' : '3D diabetes management concept',
          },
        ];
      case 4:
        return [
          {
            src: '/assets/images/vaccination-appointment.png',
            alt: language === 'ar' ? 'فريق طبي يهتم بصحة المرأة' : 'Women health checkup concept',
          },
          {
            src: '/assets/images/portrait-3d-female.png',
            alt: language === 'ar' ? 'تصميم ثلاثي الأبعاد لامرأة' : '3D female portrait illustration',
          },
        ];
      case 7:
        return [
          {
            src: '/assets/images/3d-calendar-icon.png',
            alt: language === 'ar' ? 'أيقونة تقويم ثلاثية الأبعاد للتحضير للتحاليل' : '3D calendar icon for test preparation',
          },
          {
            src: '/assets/images/blood-test-tube.png',
            alt: language === 'ar' ? 'أنبوب اختبار دم للتحضير للتحاليل' : 'Blood test tube illustration',
          },
        ];
      case 8:
        return [
          {
            src: '/assets/images/hermon.png',
            alt: language === 'ar' ? 'رسم توضيحي للهرمونات' : 'Hormone illustration',
          },
          {
            src: '/assets/images/Noi.png',
            alt: language === 'ar' ? 'مفهوم ثلاثي الأبعاد لصحة الهرمونات' : '3D hormone health concept',
          },
        ];
      case 9:
        return [
          {
            src: '/assets/images/3d-illustratio.png',
            alt: language === 'ar' ? 'مجسم ثلاثي الأبعاد لفحص الحساسية' : '3D allergy test illustration',
          },
          {
            src: '/assets/images/Pollen-grains.png',
            alt: language === 'ar' ? 'حبوب لقاح مسببة للحساسية' : 'Pollen grains allergen image',
          },
        ];
      case 10:
        return [
          {
            src: '/assets/images/blood-pressure.png',
            alt: language === 'ar' ? 'جهاز قياس ضغط الدم' : 'Blood pressure monitor illustration',
          },
          {
            src: '/assets/images/hypotension-hypertension.png',
            alt: language === 'ar' ? 'مخطط ارتفاع وانخفاض ضغط الدم' : 'Hypertension vs hypotension diagram',
          },
        ];
      case 6:
        return [
          {
            src: '/assets/images/3d-rendering-shows.png',
            alt: language === 'ar' ? 'تصوير ثلاثي الأبعاد للكبد والكلى' : '3D rendering of liver and kidneys',
          },
          {
            src: '/assets/images/istockphoto.png',
            alt: language === 'ar' ? 'رسم توضيحي للكبد والكلى' : 'Liver and kidney medical illustration',
          },
        ];
      default:
        return null;
    }
  }, [post, language]);

  const copyText = async (text: string) => {
    if (!text) return false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    } catch (error) {
      console.warn('Copy failed:', error);
      return false;
    }
  };

  const handleShare = async () => {
    if (!post) return;
    if (!shareText) return;
    const sharePayload = {
      title: post.title,
      text: shareText,
    };

    if (navigator.share) {
      try {
        await navigator.share({
          ...sharePayload,
          url: currentUrl,
        });
        showToast(language === 'ar' ? 'تم فتح نافذة المشاركة.' : 'Share dialog opened.', 'success');
        return;
      } catch (error) {
        console.warn('Share failed, falling back to clipboard:', error);
      }
    }

    const copied = await copyText(shareText);
    if (copied) {
      showToast(language === 'ar' ? 'تم نسخ النص للمشاركة.' : 'Share text copied.', 'success');
    } else {
      showToast(language === 'ar' ? 'تعذر نسخ النص. يرجى النسخ يدوياً.' : 'Could not copy text. Please copy manually.', 'error');
    }
  };

  const handleCopy = async () => {
    if (!shareText) return;
    const copied = await copyText(shareText);
    if (copied) {
      showToast(language === 'ar' ? 'تم نسخ النص للمشاركة.' : 'Share text copied.', 'success');
    } else {
      showToast(language === 'ar' ? 'تعذر نسخ النص. حاول مرة أخرى.' : 'Could not copy text. Please try again.', 'error');
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-light-primary dark:bg-dark-primary">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-dark-text">
            {t('blog.noPostsFound')}
          </h1>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-brand-purple-base dark:text-brand-orange-base hover:underline"
          >
            <BackArrow size={20} />
            {t('common.back')} {t('blog.title')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-light-primary dark:bg-dark-primary">
      {/* Hero Section with Image */}
      <section className="relative">
        {/* Image */}
        <div className="h-[60vh] min-h-[400px] bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-orange-base dark:from-brand-orange-base dark:via-brand-orange-light dark:to-brand-purple-base relative overflow-hidden">
          {post.image ? (
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white/30">
                <div className="w-24 h-24 mx-auto mb-4 border-4 border-white/30 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-12 h-12"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium">Image Placeholder</p>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-8 md:p-12">
          <div className="container mx-auto px-4">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 mb-6 text-white/80 hover:text-white transition-colors"
            >
              <BackArrow size={20} />
              {t('common.back')} {t('blog.title')}
            </Link>
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-white/20 backdrop-blur-sm text-white">
                  <Tag size={14} />
                  {getCategoryName(post.category)}
                </span>
                {post.readTime && (
                  <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-white/20 backdrop-blur-sm text-white">
                    <Clock size={14} />
                    {post.readTime} {t('blog.readTime')}
                  </span>
                )}
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white leading-tight">
                {post.title}
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mb-6">
                {post.excerpt}
              </p>
              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <User size={18} />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={18} />
                  <span>{formatDate(post.date)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 hover:text-white transition-colors"
                    aria-label="Share"
                  >
                    <Share2 size={18} />
                    <span className="hidden md:inline">{language === 'ar' ? 'مشاركة' : 'Share'}</span>
                  </button>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-2 hover:text-white transition-colors"
                    aria-label="Copy share text"
                  >
                    <Copy size={18} />
                    <span className="hidden md:inline">{language === 'ar' ? 'نسخ النص' : 'Copy Text'}</span>
                  </button>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedShareText}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${encodedUrl ? '' : 'pointer-events-none opacity-40'}`}
                    aria-label="Share on Facebook"
                  >
                    <Facebook size={16} />
                    <span className="hidden lg:inline">{language === 'ar' ? 'فيسبوك' : 'Facebook'}</span>
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodedShareText}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${encodedShareText ? '' : 'pointer-events-none opacity-40'}`}
                    aria-label="Share on WhatsApp"
                  >
                    <MessageCircle size={16} />
                    <span className="hidden lg:inline">{language === 'ar' ? 'واتساب' : 'WhatsApp'}</span>
                  </a>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <article className="prose prose-lg dark:prose-invert max-w-none">
              {post.content && post.content.length > 0 ? (
                <div className="space-y-6">
                  {post.content.map((paragraph, index) => (
                    <ScrollReveal key={index} delay={index * 0.1}>
                      <p className="text-lg leading-relaxed text-light-text-secondary dark:text-dark-text-secondary">
                        {paragraph}
                      </p>
                    </ScrollReveal>
                  ))}
                </div>
              ) : (
                <ScrollReveal>
                  <p className="text-lg leading-relaxed text-light-text-secondary dark:text-dark-text-secondary">
                    {post.excerpt}
                  </p>
                </ScrollReveal>
              )}

              {/* Additional Images */}
              {post.id === 5 && post.image ? (
                <ScrollReveal delay={0.2}>
                  <div className="my-12">
                    <div className="relative w-full max-w-4xl mx-auto rounded-xl overflow-hidden border border-light-border dark:border-dark-border shadow-xl bg-light-secondary dark:bg-dark-secondary p-4">
                      <img 
                        src={post.image} 
                        alt="مكونات الدم - Blood Composition Infographic"
                        className="w-full h-auto object-contain rounded-lg"
                        loading="lazy"
                        style={{
                          imageRendering: 'auto'
                        }}
                      />
                    </div>
                  </div>
                </ScrollReveal>
              ) : articleGallery ? (
                <div className="my-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {articleGallery.map((image, index) => (
                    <ScrollReveal key={image.src} delay={0.2 + index * 0.1}>
                      <div className="relative h-64 rounded-xl overflow-hidden border border-light-border dark:border-dark-border bg-light-secondary dark:bg-dark-secondary">
                        <img 
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              ) : (
                <div className="my-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[0, 1].map((index) => (
                    <ScrollReveal key={index} delay={0.2 + index * 0.1}>
                      <div className="relative h-64 rounded-xl overflow-hidden border border-light-border dark:border-dark-border bg-light-secondary dark:bg-dark-secondary">
                        <img
                          src={defaultPlaceholderImage}
                          alt={language === 'ar' ? 'معامل الهامي' : 'Elhamy Lab'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              )}

              {/* Call to Action */}
              <ScrollReveal delay={0.4}>
                <div className="mt-12 p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl border border-light-border dark:border-dark-border">
                  <h3 className="text-2xl font-bold mb-4 text-light-text dark:text-dark-text">
                    {t('common.book')}
                  </h3>
                  <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6">
                    {t('blog.subtitle')}
                  </p>
                  <Link
                    to="/home-visit-request"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-purple-base dark:bg-brand-orange-base text-white rounded-lg hover:bg-brand-purple-light dark:hover:bg-brand-orange-light transition-colors font-semibold"
                  >
                    {t('common.book')}
                  </Link>
                </div>
              </ScrollReveal>
            </article>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-light-secondary dark:bg-dark-secondary">
          <div className="container mx-auto px-4">
            <ScrollReveal>
              <h2 className="text-3xl font-bold mb-8 text-light-text dark:text-dark-text">
                {t('blog.relatedPosts') || 'Related Posts'}
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <ScrollReveal key={relatedPost.id} delay={index * 0.1}>
                  <Link
                    to={`/blog/${relatedPost.id}`}
                    className="block bg-light-primary dark:bg-dark-primary rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border border-light-border dark:border-dark-border group"
                  >
                    <div className="h-48 bg-gradient-to-br from-brand-purple-base to-brand-orange-base relative overflow-hidden">
                      {relatedPost.image ? (
                        <img 
                          src={relatedPost.image} 
                          alt={relatedPost.title}
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-all" />
                      <div className="absolute top-4 left-4">
                        <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-white/90 dark:bg-dark-primary/90 text-brand-purple-base dark:text-brand-orange-base backdrop-blur-sm">
                          {getCategoryName(relatedPost.category)}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-light-text dark:text-dark-text group-hover:text-brand-purple-base dark:group-hover:text-brand-orange-base transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} />
                          {formatDate(relatedPost.date)}
                        </div>
                        {relatedPost.readTime && (
                          <div className="flex items-center gap-2">
                            <Clock size={14} />
                            {relatedPost.readTime} {t('blog.readTime')}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;

